import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NbPopoverDirective } from '@nebular/theme';
import { TableList } from '../../core/table-list/table-list';
import { GenericTableHeader } from '../types/generic-table.types';
import { TableListOptions } from '../types/table-list.types';
import { SortDirection, SortEvent } from '../types/sort.types';
import _ from 'lodash';
import moment from 'moment-timezone/builds/moment-timezone-with-data.min.js'
import { ActivatedRoute, Router } from '@angular/router';
import { TimeZone } from '../types/users.types';
import { LayoutService } from '../services/layout.service';
import { environment } from '../../../environments/environment';
import { Moment } from 'moment';
import { parseDateFromString } from '../utils/Utils';

@Component({
  standalone: false,
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
})
export class GenericTableComponent extends TableList<any> implements OnChanges, OnInit, AfterViewInit {
  @Input()
  class: string;
  @Input()
  loading: boolean = false;
  @Input()
  loadingDetails: boolean = false;
  @Input()
  totalElements: number = 100000;
  @Input()
  headersList: GenericTableHeader[];
  @Input()
  detailsHeaders: GenericTableHeader[];
  @Input()
  detailsCustomRenderer: any;
  @Input()
  toggleDetailsTooltip: string;
  @Input()
  noDetailsDataMessage: string;
  @Input()
  rows: any[];
  @Input()
  route: string | undefined;
  @Input()
  routeQueryParams?: any;
  @Input()
  searchable: boolean;
  doneTypingInterval: number = 400; // Used to avoid calling SEARCH function many repeated times
  typingTimer: any;
  @Input()
  addAction: any;
  @Input()
  exportAction: any;
  @Input()
  exporting: boolean;
  @Input()
  hidePagination: boolean;
  @Input()
  fixedSize: number;
  @Input()
  dateFiltering = false;
  @Input()
  dateTimeFiltering = false;
  @Input()
  optionalDateFiltering = false;
  dateRange: any;
  @Input()
  noDateFilters: boolean;
  @ViewChild('dateInput') dateInput: any;
  @ViewChild('datePicker') datePicker: any;
  @Input()
  writePermissions: boolean = true;
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;
  @ViewChild('tableContainer') tableContainer: any;
  @ViewChild('headerContainer') headerContainer: any;
  @Input()
  headerHeight: string;
  rowExpanded: any;

  regexp = /\./g;

  mousePosition = {
    x: 0,
    y: 0,
  };

  @Output()
  dateRangeEmitter = new EventEmitter<any>();

  @Output()
  detailsEmitter = new EventEmitter<any>();
  
  @Input()
  filtersByHeader: {} = {};
  filterableInputs: any = {};
  currentFilterableValue = '';
  showFilterablePopover = false;

  @Output()
  filterEmitter = new EventEmitter<any>();

  @Input()
  isSelectable: boolean = false;
  selectedRow: number;
  selectedRowDetail: number;

  @Input()
  paginateDetails: boolean;
  @Input()
  totalDetailsElements: number;
  @Input()
  detailsPage: number = 1;
  @Input()
  detailsSize: number = 50;
  @Output()
  detailsPaginationEmitter = new EventEmitter<any>();

  @Input()
  innerTable: boolean = false; // used for some paginations in internal tables
  @Output()
  innerTablePaginationEmitter = new EventEmitter<any>();

  @Output()
  selectAllCheckboxEmitter = new EventEmitter<boolean>();
  selectAllChecked: boolean = false;

  doubleClickActivated: boolean = false;

  minAllowedDate: Date;
  maxAllowedDate: Date = moment().add(1, 'days').toDate();

  @Input()
  allowFreeDates: boolean = false; // allows all dates
  @Input()
  disableDateFilter: boolean = false;

  @ViewChild('startDatetimePicker') startDatetimePicker: any;
  @ViewChild('endDatetimePicker') endDatetimePicker: any;
  datetimeRange: any = {};
  nowDatetime: any = moment().endOf('day').toDate();
  validDateRange: boolean = true;
  timeZone: TimeZone;
  API_DATE_FORMAT = environment.mexExchange.api.dateFormat;
  @Input()
  subtotals: [];
  private readonly EMPTY_STYLE = {};
  
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private layoutService: LayoutService,
  ) {
    super();

    this.layoutService.getTimeZoneSubject().subscribe((timeZone) => {
      if (timeZone) {
        this.timeZone = timeZone;
        if (this.dateTimeFiltering) {
          this.setDatetimeRangeFromLocalStorage();
          this.handleRangeChange(this.datetimeRange);
        }
      }
    });
  }

  ngOnInit(): void {
    this.setPaginationSize();

    if (this.allowFreeDates) {
      this.minAllowedDate = null;
      this.maxAllowedDate = null;
    }

    this.activatedRoute.queryParams.subscribe((params) => {
      let tableOptions: TableListOptions = {};
      if (params.page && !this.hidePagination) {
        tableOptions.page = parseInt(params.page, 10);
      } else {
        tableOptions.page = this.hidePagination ? 0 : 1;
      }
      if (params.search) {
        tableOptions.searchTerm = params.search;
      }
      this.tableOptions = tableOptions;
      if (this.fixedSize) {
        this._fixedSize = this.fixedSize;
      }
    });

    if (this.headersList) {
      // Check if there is a previous sort for this table saved into localStorage
      if (this.class) {
        const key = this.class;
        const sortArrayStr = localStorage.getItem('tableSort') || '{}';
        const sortArray = JSON.parse(sortArrayStr);
        const savedSort = sortArray[key];
        if (savedSort) {
          const sortHeader = savedSort.sortName || savedSort.column;
          const columnExist = this.headersList.filter(header => header.name === sortHeader || header.sortName === sortHeader)[0];
          if (columnExist) {
            this.defaultSortHeader = sortHeader;
            this.defaultDirection = savedSort.direction || '';
            if (this.hidePagination) {
              this.localSort();
            }
          }
        }

        // check if there are filters applied in localStorage for current blotter
        const filtersStr = localStorage.getItem('blotterFilters') || '{}';
        const filters = JSON.parse(filtersStr);
        if (filters[key]) {
          this.filterableInputs = filters[key];
        }
      }
      this.headersList.forEach((header) => {
        if (header.defaultSort && !this.defaultSortHeader) {
          this.defaultSortHeader = header.sortName || header.name;
          this.defaultDirection = header.defaultDirection || '';
          if (this.hidePagination) {
            this.localSort();
          }
        }

        if (this.filtersByHeader && this.filtersByHeader[header.name]) {
          if (!this.filterableInputs[header.name]) {
            this.filterableInputs[header.name] = [];
          }
        }
      });
    }

    // make the input to take saved date range filter for the table prefixed by table class to make it unique
    if (this.dateFiltering || this.dateTimeFiltering) {
      const previousStartDate = localStorage.getItem(`${this.class}-startDate-filter`);
      const previousEndDate = localStorage.getItem(`${this.class}-endDate-filter`);
      if (this.dateFiltering) {
        // regular date filtering
        if (previousStartDate && previousEndDate) {
          this.dateRange = { start: parseDateFromString(previousStartDate), end: parseDateFromString(previousEndDate) };
        } else { 
          this.noDateFilters = true;
        }
        this.handleRangeChange(this.dateRange);
      } else {
        // date time filtering
        if (previousStartDate && previousEndDate) {
          this.setDatetimeRangeFromLocalStorage();
          this.handleRangeChange(this.datetimeRange);
        } else {
          this.handleRangeChange(null, true);
          setTimeout(() => this.setDatetimeRangeFromLocalStorage(), 0);
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.calculateHeight();
    this.datePicker?.blur$.subscribe(() => {
      this.dateInput.nativeElement.value = !this.noDateFilters ? moment(this.startDate).format('MM/DD/YY') + ' - ' + moment(this.endDate).format('MM/DD/YY') : '';
      this.datePicker.pickerRef.instance.range = null;
    });
    this.startDatetimePicker?.blur$.subscribe(() => {
      this.setStartDatetime(this.startDatetimePicker.value);
    });
    this.endDatetimePicker?.blur$.subscribe(() => {
      this.setEndDatetime(this.endDatetimePicker.value);
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.calculateHeight();
  }

  calculateHeight() {
    // used to have the footer pagination controls always visible
    setTimeout(() => {
      const headerContainerHeight = this.headerHeight ? this.headerHeight : this.headerContainer.nativeElement.offsetHeight;
      const extraComponentsHeight = this.hidePagination ? 121 : 168;
      const height = (window.innerHeight - headerContainerHeight - extraComponentsHeight) + 'px';
      this.tableContainer.nativeElement.style.maxHeight = height;
    }, 5);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.rows && this.rows && (changes.rows.currentValue?.length !== changes.rows.previousValue?.length ||
      JSON.stringify(changes.rows.currentValue) !== JSON.stringify(changes.rows.previousValue))) || changes.totalElements || changes.loadingDetails) {

      // if there are filters applied
      Object.keys(this.filterableInputs).forEach( header => {
        this.loading = false;
        if (this.filterableInputs[header].length) {
          const filtersByHeader = this.filterableInputs[header];
          this.rows = this.rows.filter(row => filtersByHeader.includes(row[header]));
        }
      });

      this.data = this.rows;

      if (this.rows && this.hidePagination) {
        this.totalElements = this.data.length;
        this.size = this.data.length;
        this.localSort();
      }
    }
  }

  onMouseDown($event: MouseEvent) {
    this.mousePosition.x = $event.screenX;
    this.mousePosition.y = $event.screenY;
  }

  goDetails($event: MouseEvent, row: any): void {
    // we give some time in case it is a double click event
    setTimeout(() => {
      if (!this.doubleClickActivated) {
        // Single click action
        const xDiff = Math.abs(this.mousePosition.x - $event.screenX);
        const yDiff = Math.abs(this.mousePosition.y - $event.screenY);
        if (xDiff <= 5 && yDiff <= 5 && this.route) {
          this.loading = true;
          // we store table query parameters to be used later when going back on BreadCrumb link click
          this.activatedRoute.queryParams.subscribe((params) => {
            if (!params.skipStorage) {
              localStorage.setItem('previousTableParams', JSON.stringify(params));
            }
          });
          const index = this.route.search(':');
          if (index !== -1) {
            const parameter = this.route.substring(index + 1);
            this.router.navigate([`${this.route.substring(0, index)}${row[parameter]}`], { queryParams: this.routeQueryParams });
          } else {
            this.router.navigate([`${this.route}`], { queryParams: this.routeQueryParams });
          }
        }        
      }
    }, 200);
  }

  parseHeader(row: any, header: string) {
    const entry = this.headersList.findIndex((h) => h.name === header);
    const entryHeader = this.headersList[entry];
    if (entry !== -1 && entryHeader.customRender) {
      const value = entryHeader.customRender(row, entryHeader);
      if (typeof row._customRender === 'undefined') {
        row._customRender = {};
      }
      row._customRender[entryHeader.name] = value;
      return value;
    } else {
      const count = [...header.matchAll(this.regexp)].length;
      if (count === 0) {
        return typeof row[header] === 'undefined' || row[header] === null || row[header] === '' ? '-' : Array.isArray(row[header]) ?
          row[header].join(', ') : row[header];
      } else {
        const list = header.split('.', count + 1);
        if (row[list[0]]) {
          list.forEach((item) => {
            row = row[item];
          });
          return row;
        } else {
          return 'N/A';
        }
      }
    }
  }

  toggleSelectAllRows(select: boolean) {
    this.selectAllChecked = select;
    this.rows.forEach(row => row.selected = select);
    this.selectAllCheckboxEmitter.emit(select);
  }

  cleanInput(): void {
    this.searchTerm = '';
  }

  set searchTerm(searchTerm: string) {
    super.searchTerm = searchTerm;

    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      // Adds 'searchTerm' parameter to url router,
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          search: searchTerm.length ? searchTerm : null,
          page: null,
          runQuery: true,
        },
        queryParamsHandling: 'merge',
      });
    }, this.doneTypingInterval);

  }

  get searchTerm(): string {
    return super.searchTerm;
  }

  searchByEnter(event: any) {
    if (event.key === 'Enter') {
      // Add a random refresh parameter that will be ignored just to refresh the same search
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          refresh: Math.random(),
          page: null,
          runQuery: true,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  setPaginationSize() {
    if (this.hidePagination) {
      this.size = this.rows ? this.rows.length : 0;
    } else {
      if (this.class) {
        const key = this.class;

        const paginationsStr = localStorage.getItem('tablePaginations') || '{}';
        const paginations = JSON.parse(paginationsStr);

        const sizeStr = paginations[key] || 50;

        this.size = parseInt(sizeStr, 10);
      } else {
        this.size = 50;
      }
    }
  }

  onItemsSizeChange() {
    if (this.class) {
      const key = this.class;
      let paginationsStr = localStorage.getItem('tablePaginations') || '{}';
      const paginations = JSON.parse(paginationsStr);

      paginations[key] = this.size.toString();
      paginationsStr = JSON.stringify(paginations);
      localStorage.setItem('tablePaginations', paginationsStr);
    }

    this.page = 1;
    this.setPage(this.page, true);
  }

  setPage(page: number, setSize?: boolean) {
    this.selectedRow = null;
    this.selectedRowDetail = null;

    if (!this.innerTable) {
    // Adds parameters to url router,
    const queryParams = setSize ? { page: page, size: this.size } : { page: page };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
    } else {
      this.innerTablePaginationEmitter.emit({ page });
    }
  }

  setDetailsPage(page: number) {
    if (this.detailsPage !== page) {
      this.detailsPage = page;
      this.detailsPaginationEmitter.emit({ page });
    }
  }

  onSort({ column, direction }: SortEvent) {
    const headers = this.headers || [];
    let sortName;
    headers.forEach((header) => {
      // clean all the sortable options but the one clicked
      if (header.appSortable !== column) {
        header.direction = SortDirection.none;
      } else {
        sortName = header.sortName;
      }
    });

    if (!direction) {
      // remove the sort option
      this.sort = {};
    } else {
      this.sort = { column, direction, sortName };
    }

    if (this.class) {
      const key = this.class;
      let sortArrayStr = localStorage.getItem('tableSort') || '{}';
      const sortArray = JSON.parse(sortArrayStr);

      sortArray[key] = this.sort;
      sortArrayStr = JSON.stringify(sortArray);
      localStorage.setItem('tableSort', sortArrayStr);

      // Adds parameters to url router,
      const queryParams = this.sort?.column ? { runQuery: true, sort: this.sort.column + ',' + this.sort.direction } : { runQuery: true, sort: null };
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  checkStyle(row: any, header: any) {
    if (header.styleRow) {
      const headerName = header.styleRowConditionalHeader ? header.styleRowConditionalHeader : header.name;
      const value = this.parseHeader(row, headerName);
      const style = header.styleRow[value];
      if (style) {
        return style;
      } else {
        return this.EMPTY_STYLE;
      }
    } else {
      return this.EMPTY_STYLE;
    }
  }

  showFilterablePopUp(event: Event, nameHeader: string) {
    this.currentFilterableValue = nameHeader;
    this.showFilterablePopover = true;
  }

  closeFilterablePopopver(popover: any) {
    this.showFilterablePopover = false;
    this.currentFilterableValue = '';
  }

  setFilter(value: string) {
    this.loading = true;
    if (this.filterableInputs[this.currentFilterableValue].indexOf(value) < 0) {
      this.filterableInputs[this.currentFilterableValue].push(value);
      this.saveBlotterFilters();
      this.filterEmitter.emit({ currentFilter: value, subscribe: true });
    } else {
      this.filterableInputs[this.currentFilterableValue] = _.without(this.filterableInputs[this.currentFilterableValue], value);
      this.saveBlotterFilters();
      this.filterEmitter.emit({ currentFilter: value, subscribe: false });
    }
  }

  saveBlotterFilters() {
    // keep current blotter filters in localStorage
    let tableFilters = localStorage.getItem('blotterFilters') || '{}';
    const filters = JSON.parse(tableFilters);
    filters[this.class] = this.filterableInputs;
    localStorage.setItem('blotterFilters', JSON.stringify(filters));
  }

  copyToClipboard($event: any) {
    this.doubleClickActivated = true;
    // if the target is the highlighted span, we get the parent string
    const target = $event.target.tagName === 'SPAN' ? $event.target.parentNode : $event.target;
    navigator.clipboard.writeText(target.textContent);

    target.classList.add('highlight');
    setTimeout(() => {
      target.classList.remove('highlight');
      this.doubleClickActivated = false;
    }, 300);
  }

  handleRangeChange(dateRange: any, forceStorage?: boolean) {
    // we calculate max calendar end date based on start date selected
    if (dateRange && dateRange.start && !this.allowFreeDates) {
      const startDate = moment().utc(dateRange.start).startOf('date');
      const endDateBasedOnStart = startDate.add(3, 'month').toDate();
      this.maxAllowedDate = endDateBasedOnStart < this.maxAllowedDate ? endDateBasedOnStart : moment().add(1, 'days').toDate();
    }

    if (!dateRange || (dateRange.start && dateRange.end)) {
      if (!dateRange && this.optionalDateFiltering) {
        this.dateRangeEmitter.emit(null);
      } else {
        if (this.dateFiltering) {
          // regular date filtering
          this.startDate = !dateRange ? moment().tz("America/New_York").startOf('date').utc() : moment(dateRange.start).startOf('date').utc();
          this.endDate = !dateRange ? moment().tz("America/New_York").endOf('date').utc() : moment(dateRange.end).endOf('date').utc();
        } else {
          // date time filtering
          if (!dateRange ) {
            const limit = moment().tz("America/New_York").set( { hour: 17, minute: 0, second: 0} );
            if (moment().tz("America/New_York").isAfter(limit)) {
              // if today current time is after 17hs
              this.startDate = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0}).utc();
              this.endDate = moment().tz("America/New_York").add(1, 'days').set({ hour: 17, minute: 0, second: 0}).utc();
            } else {
              // if today current time is before 17hs
              this.startDate = moment().tz("America/New_York").subtract(1, 'days').set({ hour: 17, minute: 0, second: 0 }).utc();
              this.endDate = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0}).utc();
            }
          } else {
            this.startDate = moment(dateRange.start.getTime());
            this.endDate = moment(dateRange.end.getTime());
          }
        }

        if (dateRange || forceStorage) {
          // Persist start and end date selection
          localStorage.setItem(`${this.class}-startDate-filter`, this.timeZone === TimeZone.UTC ? this.startDate.format(this.API_DATE_FORMAT) :
            this.startDate.utc().format(this.API_DATE_FORMAT));
          localStorage.setItem(`${this.class}-endDate-filter`, this.timeZone === TimeZone.UTC ? this.endDate.format(this.API_DATE_FORMAT) :
            this.endDate.utc().format(this.API_DATE_FORMAT));
          this.noDateFilters = false;
        }
  
        this.dateRangeEmitter.emit({
          startDate: this.startDate,
          endDate: this.endDate,
        });
        this.selectedRow = null;
        this.selectedRowDetail = null;
      }
    }
  }

  enableClearDates(event: any) {
    this.noDateFilters = !this.dateInput.nativeElement.value;
  }

  clearDates() {
    this.dateInput.nativeElement.value = '';
    this.startDate = undefined;
    this.endDate = undefined;
    this.dateRange = null;
    this.selectedRow = null;
    this.selectedRowDetail = null;
    this.noDateFilters = true;
    if (this.optionalDateFiltering) {
      this.dateRangeEmitter.emit(null);
    } else {
      const startDate = moment().startOf('date').utc();
      const endDate = moment().endOf('date').utc();
      this.dateRangeEmitter.emit({ startDate, endDate });
    }

    // Clean up saved date range
    localStorage.removeItem(`${this.class}-startDate-filter`);
    localStorage.removeItem(`${this.class}-endDate-filter`);
  }

  setDatetimeRangeFromLocalStorage() {
    const startDate = localStorage.getItem(`${this.class}-startDate-filter`);
    const endDate = localStorage.getItem(`${this.class}-endDate-filter`);
    if (startDate && endDate){
      const start = this.timeZone === TimeZone.UTC ? moment(startDate).toDate() : parseDateFromString(startDate);
      const end = this.timeZone === TimeZone.UTC ? moment(endDate).toDate() : parseDateFromString(endDate);
      this.datetimeRange = { start, end};
    }
  }

  setStartDatetime(startDate: any) {
    this.datetimeRange.start = startDate;
    this.checkDateRangeValidity();
    if (this.checkDateRangeValidity()) {
      this.handleRangeChange(this.datetimeRange);
    }
  }

  setEndDatetime(endDate: any) {
    this.datetimeRange.end = endDate;
    if (this.checkDateRangeValidity()) {
      this.handleRangeChange(this.datetimeRange);
    }
  }

  checkDateRangeValidity() {
    this.validDateRange = !this.datetimeRange.start && !this.datetimeRange.end ? true : this.datetimeRange.start && !this.datetimeRange.end ? false :
      !this.datetimeRange.start && this.datetimeRange.end ? false : this.datetimeRange.start.getTime() <= this.datetimeRange.end.getTime();
    if (!this.validDateRange) {
      this.dateRangeEmitter.emit({ invalid: true });
    }
    return this.validDateRange;
  }

  clearStartDatetime() {
    this.startDate = undefined;
    this.selectedRow = null;
    this.selectedRowDetail = null;

    let startDate;
    const limit = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0} );
    if (moment().tz("America/New_York").isAfter(limit)) {
      // if today current time is after 17hs
      startDate = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0}).utc();
    } else {
      // if today current time is before 17hs
      startDate = moment().tz("America/New_York").subtract(1, 'days').set({ hour: 17, minute: 0, second: 0 }).utc();
    }

    let formattedStart;
    if (this.timeZone === TimeZone.UTC) {
      formattedStart = startDate.format(this.API_DATE_FORMAT);
      this.datetimeRange.start = moment(formattedStart).toDate();
    } else {
      formattedStart = startDate.utc().format(this.API_DATE_FORMAT);
      this.datetimeRange.start = parseDateFromString(formattedStart);
    }

    if (this.checkDateRangeValidity()) {
      this.handleRangeChange(this.datetimeRange);
    }
  }

  clearEndDatetime() {
    this.endDate = undefined;
    this.selectedRow = null;
    this.selectedRowDetail = null;

    let endDate;
    const limit = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0} );
    if (moment().tz("America/New_York").isAfter(limit)) {
      // if today current time is after 17hs
      endDate = moment().tz("America/New_York").add(1, 'days').set({ hour: 17, minute: 0, second: 0}).utc();
    } else {
      // if today current time is before 17hs
      endDate = moment().tz("America/New_York").set({ hour: 17, minute: 0, second: 0}).utc();
    }
    
    let formattedEnd;
    if (this.timeZone === TimeZone.UTC) {
      formattedEnd = endDate.format(this.API_DATE_FORMAT);
      this.datetimeRange.end = moment(formattedEnd).toDate();
    } else {
      formattedEnd = endDate.utc().format(this.API_DATE_FORMAT);
      this.datetimeRange.end = parseDateFromString(formattedEnd);
    }

    if (this.checkDateRangeValidity()) {
      this.handleRangeChange(this.datetimeRange);
    }
  }

  /* Methods for table with nested childs */
  showDetails($event: MouseEvent, row: any) {
    $event.preventDefault();
    $event.stopPropagation();
    const xDiff = Math.abs(this.mousePosition.x - $event.screenX);
    const yDiff = Math.abs(this.mousePosition.y - $event.screenY);
    if (xDiff <= 5 && yDiff <= 5) {
      if (this.rowExpanded && !row.expanded) {
        this.rowExpanded.expanded = false;
      }
      row.expanded = !row.expanded;
      this.rowExpanded = row;
      this.selectedRowDetail = null;
      this.detailsPage = 1;
      this.detailsEmitter.emit({ row, clear: !row.expanded });
    }
  }

}
